import { useRef, useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../contexts/LanguageContext";
import html2canvas from "html2canvas";
import { getAuthHeaders } from "../utils/api";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Elegant SVG planetary symbols with gold gradient
const PlanetarySymbol = ({ planet, size = 32 }) => {
    const planetData = {
        sun: { path: "M12 3V4.5M12 19.5V21M21 12H19.5M4.5 12H3M18.364 5.636L17.303 6.697M6.697 17.303L5.636 18.364M18.364 18.364L17.303 17.303M6.697 6.697L5.636 5.636M12 8C9.79 8 8 9.79 8 12C8 14.21 9.79 16 12 16C14.21 16 16 14.21 16 12C16 9.79 14.21 8 12 8Z", viewBox: "0 0 24 24" },
        moon: { path: "M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z", viewBox: "0 0 24 24" },
        mercury: { path: "M12 2V4M12 4C9.24 4 7 6.24 7 9C7 11.76 9.24 14 12 14C14.76 14 17 11.76 17 9C17 6.24 14.76 4 12 4M8 14V18M16 14V18M12 14V22M8 22H16", viewBox: "0 0 24 24" },
        venus: { path: "M12 2C8.69 2 6 4.69 6 8C6 11.31 8.69 14 12 14C15.31 14 18 11.31 18 8C18 4.69 15.31 2 12 2M12 14V22M8 18H16", viewBox: "0 0 24 24" },
        mars: { path: "M16 2H22V8M22 2L13 11M12 22C7.58 22 4 18.42 4 14C4 9.58 7.58 6 12 6C16.42 6 20 9.58 20 14C20 18.42 16.42 22 12 22Z", viewBox: "0 0 24 24" },
        jupiter: { path: "M4 12H20M13 4V20M8 4C8 4 11 8 11 12C11 16 8 20 8 20", viewBox: "0 0 24 24" },
        saturn: { path: "M4 6H12M8 6V14C8 16.21 9.79 18 12 18C14.21 18 16 16.21 16 14C16 11.79 14.21 10 12 10M20 18L16 22", viewBox: "0 0 24 24" },
        uranus: { path: "M12 2V8M12 16V22M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12M6 12H18", viewBox: "0 0 24 24" },
        neptune: { path: "M12 2V22M8 6L12 2L16 6M6 10H18M8 14C8 14 10 18 12 18C14 18 16 14 16 14", viewBox: "0 0 24 24" },
        pluto: { path: "M12 22V10M12 10C14.21 10 16 8.21 16 6C16 3.79 14.21 2 12 2C9.79 2 8 3.79 8 6C8 8.21 9.79 10 12 10M6 10H18M6 14C6 14 9 18 12 18C15 18 18 14 18 14", viewBox: "0 0 24 24" }
    };
    
    const data = planetData[planet.toLowerCase()] || planetData.sun;
    const gradientId = `planet-grad-${planet}-${Math.random().toString(36).substr(2, 5)}`;
    
    return (
        <svg width={size} height={size} viewBox={data.viewBox} fill="none" style={{ filter: 'drop-shadow(0 0 6px rgba(212, 175, 55, 0.5))' }}>
            <defs>
                <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#FEF3C7" />
                    <stop offset="50%" stopColor="#F5D76E" />
                    <stop offset="100%" stopColor="#B8860B" />
                </linearGradient>
            </defs>
            <path d={data.path} stroke={`url(#${gradientId})`} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
};

// Translation maps for backend values
const ZODIAC_TRANSLATIONS = {
    "Aries": { en: "Aries", es: "Aries" },
    "Taurus": { en: "Taurus", es: "Tauro" },
    "Gemini": { en: "Gemini", es: "Géminis" },
    "Cancer": { en: "Cancer", es: "Cáncer" },
    "Leo": { en: "Leo", es: "Leo" },
    "Virgo": { en: "Virgo", es: "Virgo" },
    "Libra": { en: "Libra", es: "Libra" },
    "Scorpio": { en: "Scorpio", es: "Escorpio" },
    "Sagittarius": { en: "Sagittarius", es: "Sagitario" },
    "Capricorn": { en: "Capricorn", es: "Capricornio" },
    "Aquarius": { en: "Aquarius", es: "Acuario" },
    "Pisces": { en: "Pisces", es: "Piscis" }
};

const MOON_PHASE_TRANSLATIONS = {
    "New Moon": { en: "New Moon", es: "Luna Nueva" },
    "Waxing Crescent": { en: "Waxing Crescent", es: "Creciente" },
    "First Quarter": { en: "First Quarter", es: "Cuarto Creciente" },
    "Waxing Gibbous": { en: "Waxing Gibbous", es: "Gibosa Creciente" },
    "Full Moon": { en: "Full Moon", es: "Luna Llena" },
    "Waning Gibbous": { en: "Waning Gibbous", es: "Gibosa Menguante" },
    "Last Quarter": { en: "Last Quarter", es: "Cuarto Menguante" },
    "Waning Crescent": { en: "Waning Crescent", es: "Menguante" }
};

const PLANETARY_HOUR_TRANSLATIONS = {
    "Sun": { en: "Sun", es: "Sol" },
    "Moon": { en: "Moon", es: "Luna" },
    "Mars": { en: "Mars", es: "Marte" },
    "Mercury": { en: "Mercury", es: "Mercurio" },
    "Jupiter": { en: "Jupiter", es: "Júpiter" },
    "Venus": { en: "Venus", es: "Venus" },
    "Saturn": { en: "Saturn", es: "Saturno" },
    "Uranus": { en: "Uranus", es: "Urano" },
    "Neptune": { en: "Neptune", es: "Neptuno" },
    "Pluto": { en: "Pluto", es: "Plutón" },
    "North Node": { en: "North Node", es: "Nodo Norte" },
    "South Node": { en: "South Node", es: "Nodo Sur" }
};

const CONDITION_TRANSLATIONS = {
    "STEADY": { en: "STEADY", es: "ESTABLE" },
    "ACTIVE": { en: "ACTIVE", es: "ACTIVO" },
    "INTENSE": { en: "INTENSE", es: "INTENSO" },
    "PEAK": { en: "PEAK", es: "PICO" },
    "QUIET": { en: "QUIET", es: "QUIETO" },
    "DORMANT": { en: "DORMANT", es: "LATENTE" }
};

// Helper function to translate a value
const translateValue = (value, translationMap, language) => {
    if (!value) return value;
    const key = Object.keys(translationMap).find(k => 
        k.toLowerCase() === value.toLowerCase() || 
        value.toLowerCase().includes(k.toLowerCase())
    );
    if (key && translationMap[key]) {
        return translationMap[key][language] || translationMap[key]['en'] || value;
    }
    return value;
};

/**
 * Parse simple markdown (italics and bold) to JSX
 * Converts *text* to <em>text</em> and **text** to <strong>text</strong>
 */
const parseMarkdown = (text) => {
    if (!text || typeof text !== 'string') return text;
    
    const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
    
    return parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i} className="font-semibold" style={{ color: '#F5D76E' }}>{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('*') && part.endsWith('*')) {
            return <em key={i} className="italic" style={{ color: 'rgba(212, 175, 55, 0.9)' }}>{part.slice(1, -1)}</em>;
        }
        return part;
    });
};

// Gold accent color for all chambers (monochrome design)
const GOLD = '#D4AF37';

export function TwelveHousesCarousel({ birthDate, birthTime, ascendantSign, userName }) {
    const { t, language } = useLanguage();
    const scrollRef = useRef(null);
    const [housesData, setHousesData] = useState(null);
    const [housesLoading, setHousesLoading] = useState(true);  // NEW: Track initial loading state
    const [selectedHouse, setSelectedHouse] = useState(null);
    const [modalInsight, setModalInsight] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);

    // Build translated house data
    const CHAMBER_NAMES = {
        1: t('houses.chamber_identity'),
        2: t('houses.chamber_worth'),
        3: t('houses.chamber_voices'),
        4: t('houses.chamber_origins'),
        5: t('houses.chamber_creation'),
        6: t('houses.chamber_rituals'),
        7: t('houses.chamber_souls'),
        8: t('houses.chamber_rebirth'),
        9: t('houses.chamber_truth'),
        10: t('houses.chamber_legacy'),
        11: t('houses.chamber_kin'),
        12: t('houses.chamber_shadows')
    };

    const STATIC_HOUSES = [
        { number: 1, name: t('houses.identity'), chamber: CHAMBER_NAMES[1], symbol: '☉', ruler: 'Mars', theme: t('houses.theme_self'), color: GOLD },
        { number: 2, name: t('houses.worth'), chamber: CHAMBER_NAMES[2], symbol: '♀', ruler: 'Venus', theme: t('houses.theme_values'), color: GOLD },
        { number: 3, name: t('houses.voices'), chamber: CHAMBER_NAMES[3], symbol: '☿', ruler: 'Mercury', theme: t('houses.theme_communication'), color: GOLD },
        { number: 4, name: t('houses.origins'), chamber: CHAMBER_NAMES[4], symbol: '☽', ruler: 'Moon', theme: t('houses.theme_home'), color: GOLD },
        { number: 5, name: t('houses.creation'), chamber: CHAMBER_NAMES[5], symbol: '☉', ruler: 'Sun', theme: t('houses.theme_joy'), color: GOLD },
        { number: 6, name: t('houses.rituals'), chamber: CHAMBER_NAMES[6], symbol: '☿', ruler: 'Mercury', theme: t('houses.theme_health'), color: GOLD },
        { number: 7, name: t('houses.souls'), chamber: CHAMBER_NAMES[7], symbol: '♀', ruler: 'Venus', theme: t('houses.theme_partnerships'), color: GOLD },
        { number: 8, name: t('houses.rebirth'), chamber: CHAMBER_NAMES[8], symbol: '♇', ruler: 'Pluto', theme: t('houses.theme_transformation'), color: GOLD },
        { number: 9, name: t('houses.truth'), chamber: CHAMBER_NAMES[9], symbol: '♃', ruler: 'Jupiter', theme: t('houses.theme_philosophy'), color: GOLD },
        { number: 10, name: t('houses.legacy'), chamber: CHAMBER_NAMES[10], symbol: '♄', ruler: 'Saturn', theme: t('houses.theme_career'), color: GOLD },
        { number: 11, name: t('houses.kin'), chamber: CHAMBER_NAMES[11], symbol: '♅', ruler: 'Uranus', theme: t('houses.theme_community'), color: GOLD },
        { number: 12, name: t('houses.shadows'), chamber: CHAMBER_NAMES[12], symbol: '♆', ruler: 'Neptune', theme: t('houses.theme_subconscious'), color: GOLD }
    ];

    // Cache helper functions for house insights
    // Version 2: Added user_name to cache key to invalidate old "Hijo de las Estrellas" caches
    const HOUSE_CACHE_VERSION = "v2";
    const getHouseCacheKey = (houseNumber) => `astrum_house_${HOUSE_CACHE_VERSION}_${houseNumber}_${birthDate || 'guest'}_${language}`;
    
    const isHouseCacheValid = (houseNumber) => {
        const cached = localStorage.getItem(getHouseCacheKey(houseNumber));
        if (!cached) return false;
        try {
            const { timestamp } = JSON.parse(cached);
            const cachedDate = new Date(timestamp).toDateString();
            const today = new Date().toDateString();
            return cachedDate === today;
        } catch {
            return false;
        }
    };
    
    const getHouseFromCache = (houseNumber) => {
        try {
            const cached = localStorage.getItem(getHouseCacheKey(houseNumber));
            if (cached) {
                const { data } = JSON.parse(cached);
                return data;
            }
        } catch {}
        return null;
    };
    
    const saveHouseToCache = (houseNumber, data) => {
        try {
            localStorage.setItem(getHouseCacheKey(houseNumber), JSON.stringify({
                data,
                timestamp: new Date().toISOString()
            }));
        } catch {}
    };

    const fetchPlacements = useCallback(async () => {
        if (!birthDate) {
            setHousesLoading(false);
            return;
        }
        setHousesLoading(true);
        try {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_URL}/api/houses/placements`, {
                method: "POST",
                headers,
                credentials: "include",
                body: JSON.stringify({ birth_date: birthDate, birth_time: birthTime || "12:00", ascendant_sign: ascendantSign })
            });
            if (res.ok) setHousesData(await res.json());
        } catch (err) {
            console.error("Failed to fetch house placements:", err);
        } finally {
            setHousesLoading(false);
        }
    }, [birthDate, birthTime, ascendantSign]);

    useEffect(() => { fetchPlacements(); }, [fetchPlacements]);

    // Listen for language changes and clear modal state
    useEffect(() => {
        const handleLanguageChange = () => {
            console.log('[Houses] Language changed, clearing modal state');
            setSelectedHouse(null);
            setModalInsight(null);
            setModalLoading(false);
            setIsStreaming(false);
        };
        window.addEventListener('languageChanged', handleLanguageChange);
        return () => window.removeEventListener('languageChanged', handleLanguageChange);
    }, []);

    const fetchInsight = async (house) => {
        if (!birthDate) return;
        
        // Check cache first
        if (isHouseCacheValid(house.number)) {
            const cachedData = getHouseFromCache(house.number);
            if (cachedData) {
                setModalLoading(false);
                setIsStreaming(false);
                setModalInsight(cachedData);
                return;
            }
        }
        
        // Get user name from onboarding data FIRST - before setting loading state
        let userName = '';
        try {
            const onboardingData = localStorage.getItem('astral_belonging_onboarding');
            if (onboardingData) {
                const parsed = JSON.parse(onboardingData);
                userName = parsed?.userData?.name || '';
            }
        } catch (e) {
            console.warn('Could not parse onboarding data for user name');
        }
        
        // API GUARD: Do NOT fire API if userName is missing or < 3 chars
        if (!userName || userName.trim().length < 3) {
            console.log("⏳ TwelveHousesCarousel: Waiting for valid userName before API call");
            setModalLoading(false);
            setIsStreaming(false);
            return;
        }
        
        setModalLoading(true);
        setModalInsight(null);
        setIsStreaming(false);
        
        try {
            // Build URL with user_name parameter
            let url = `${API_URL}/api/houses/insight/stream?lang=${language}`;
            url += `&user_name=${encodeURIComponent(userName)}`;
            
            console.log("🏠 TwelveHousesCarousel: Fetching insight for house", house.number);
            
            const res = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ house_number: house.number, birth_date: birthDate, birth_time: birthTime || "12:00", ascendant_sign: ascendantSign })
            });
            
            if (!res.ok) throw new Error("Stream failed");
            
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let insightText = "";
            let metadata = null;
            let finalInsight = null;
            
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                
                const chunk = decoder.decode(value, { stream: true });
                const lines = chunk.split("\n");
                
                for (const line of lines) {
                    if (line.startsWith("data: ")) {
                        try {
                            const data = JSON.parse(line.slice(6));
                            if (data.type === "metadata") {
                                metadata = data;
                                setModalLoading(false);
                                setIsStreaming(true);
                                setModalInsight({ ...data, insight: "" });
                            } else if (data.type === "text") {
                                insightText += data.content;
                                finalInsight = { ...metadata, insight: insightText };
                                setModalInsight(prev => prev ? { ...prev, insight: insightText } : null);
                            } else if (data.type === "done") {
                                setIsStreaming(false);
                                // Cache the complete insight
                                if (finalInsight) {
                                    saveHouseToCache(house.number, finalInsight);
                                }
                            }
                        } catch (e) {}
                    }
                }
            }
        } catch (err) {
            console.error("Failed to fetch house insight:", err);
        } finally {
            // API KILL-SWITCH: Always stop loading states even on 400/422 errors
            setModalLoading(false);
            setIsStreaming(false);
        }
    };

    const openModal = (house) => {
        setSelectedHouse(house);
        fetchInsight(house);
    };

    const closeModal = () => {
        setSelectedHouse(null);
        setModalInsight(null);
        setIsStreaming(false);
    };

    const getHouseInfo = (num) => {
        const s = STATIC_HOUSES.find(h => h.number === num);
        if (!housesData?.houses?.[num]) return { ...s, tenants: [], visitors: [], isActive: false };
        const d = housesData.houses[num];
        return { ...s, tenants: d.tenants || [], visitors: d.visitors || [], isActive: d.is_active };
    };

    const getBadge = (h) => {
        if (!h.visitors || h.visitors.length === 0) return null;
        const heavy = ["Saturn", "Jupiter", "Pluto", "Neptune", "Uranus", "Mars"];
        if (h.visitors.some(v => heavy.includes(v.planet))) return { text: t('houses.energy_spike'), color: "#FF5722", emoji: "⚡" };
        if (h.visitors.length >= 2) return { text: t('houses.active_now'), color: "#8B5CF6", emoji: "✧" };
        return null;
    };

    const formatPlanets = (list, max) => {
        if (!list || list.length === 0) return null;
        const syms = list.slice(0, max).map(p => p.symbol).join(" ");
        return list.length > max ? `${syms} +${list.length - max}` : syms;
    };

    // Skeleton loader for houses during initial load
    const HouseSkeletonCard = ({ index }) => (
        <motion.div 
            className="flex-shrink-0" 
            style={{ scrollSnapAlign: 'start' }}
            initial={{ opacity: 0, x: 20 }} 
            animate={{ opacity: 1, x: 0 }} 
            transition={{ delay: 0.1 + index * 0.03 }}
        >
            <div className="relative w-28 h-48 rounded-lg overflow-hidden" style={{ 
                background: 'linear-gradient(180deg, #1a1a1a 0%, #050505 100%)', 
                border: '1px solid rgba(212, 175, 55, 0.2)', 
                boxShadow: '0 4px 20px rgba(0,0,0,0.5)' 
            }}>
                <div className="absolute inset-0 flex flex-col items-center justify-between p-3 pt-5">
                    {/* Pulsing skeleton elements */}
                    <motion.div 
                        className="w-12 h-2 rounded" 
                        style={{ background: 'rgba(212, 175, 55, 0.15)' }}
                        animate={{ opacity: [0.3, 0.6, 0.3] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                    />
                    <motion.div 
                        className="w-10 h-10 rounded-full" 
                        style={{ background: 'rgba(212, 175, 55, 0.1)' }}
                        animate={{ opacity: [0.2, 0.5, 0.2] }}
                        transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}
                    />
                    <div className="w-full space-y-2">
                        <motion.div 
                            className="w-16 h-3 mx-auto rounded" 
                            style={{ background: 'rgba(212, 175, 55, 0.15)' }}
                            animate={{ opacity: [0.3, 0.6, 0.3] }}
                            transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
                        />
                        <motion.div 
                            className="w-12 h-2 mx-auto rounded" 
                            style={{ background: 'rgba(212, 175, 55, 0.1)' }}
                            animate={{ opacity: [0.2, 0.4, 0.2] }}
                            transition={{ duration: 1.5, repeat: Infinity, delay: 0.4 }}
                        />
                    </div>
                </div>
                {/* Gold corner decorations */}
                <div className="absolute top-2 left-2 w-3 h-3 border-t border-l" style={{ borderColor: 'rgba(212, 175, 55, 0.2)' }} />
                <div className="absolute top-2 right-2 w-3 h-3 border-t border-r" style={{ borderColor: 'rgba(212, 175, 55, 0.2)' }} />
                <div className="absolute bottom-2 left-2 w-3 h-3 border-b border-l" style={{ borderColor: 'rgba(212, 175, 55, 0.2)' }} />
                <div className="absolute bottom-2 right-2 w-3 h-3 border-b border-r" style={{ borderColor: 'rgba(212, 175, 55, 0.2)' }} />
            </div>
        </motion.div>
    );

    return (
        <motion.div className="w-full mb-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}>
            <h2 className="text-[10px] font-inter text-primary/50 uppercase tracking-[0.25em] mb-3 px-4">{t('dashboard.twelve_houses')}</h2>
            
            <div ref={scrollRef} className="flex gap-3 overflow-x-auto scrollbar-hide px-4 pb-2" style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }} data-testid="houses-carousel">
                {/* Show skeleton loaders while loading */}
                {housesLoading && !housesData ? (
                    [...Array(6)].map((_, idx) => <HouseSkeletonCard key={`skeleton-${idx}`} index={idx} />)
                ) : (
                STATIC_HOUSES.map((sh, idx) => {
                    const house = getHouseInfo(sh.number);
                    const badge = getBadge(house);
                    const tenants = formatPlanets(house.tenants, 3);
                    const visitors = formatPlanets(house.visitors, 2);
                    
                    return (
                        <motion.div key={sh.number} className="flex-shrink-0 cursor-pointer" style={{ scrollSnapAlign: 'start' }} whileHover={{ y: -4 }} whileTap={{ scale: 0.98 }} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 + idx * 0.05 }} onClick={() => openModal(house)} data-testid={`house-card-${sh.number}`}>
                            {/* Obsidian & Gold Card Style - matching Tarot aesthetic */}
                            <div className="relative w-28 h-48 rounded-lg overflow-hidden" style={{ 
                                background: 'linear-gradient(180deg, #1a1a1a 0%, #050505 100%)', 
                                border: '1px solid rgba(212, 175, 55, 0.4)', 
                                boxShadow: '0 4px 20px rgba(0,0,0,0.5), inset 0 0 30px rgba(212, 175, 55, 0.03)' 
                            }}>
                                {/* Activity Badge - Red frame for ENERGY SPIKE, Amethyst frame for ACTIVE NOW */}
                                {badge && (
                                    <div className="absolute top-0 left-0 right-0 z-20 py-1 px-2 text-center" style={{ 
                                        background: badge.color === '#FF5722' 
                                            ? 'linear-gradient(180deg, rgba(255, 87, 34, 0.25) 0%, transparent 100%)'
                                            : badge.color === '#8B5CF6'
                                            ? 'linear-gradient(180deg, rgba(139, 92, 246, 0.25) 0%, transparent 100%)'
                                            : 'linear-gradient(180deg, rgba(212, 175, 55, 0.15) 0%, transparent 100%)',
                                        borderTop: `2px solid ${badge.color}`
                                    }}>
                                        <span className="text-[7px] font-inter uppercase tracking-wider flex items-center justify-center gap-1" style={{ color: badge.color }}>
                                            <motion.span animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1.5, repeat: Infinity }}>{badge.emoji}</motion.span>
                                            {badge.text}
                                        </span>
                                    </div>
                                )}
                                
                                {/* Gold corner decorations */}
                                <div className="absolute top-2 left-2 w-3 h-3 border-t border-l" style={{ borderColor: 'rgba(212, 175, 55, 0.5)' }} />
                                <div className="absolute top-2 right-2 w-3 h-3 border-t border-r" style={{ borderColor: 'rgba(212, 175, 55, 0.5)' }} />
                                <div className="absolute bottom-2 left-2 w-3 h-3 border-b border-l" style={{ borderColor: 'rgba(212, 175, 55, 0.5)' }} />
                                <div className="absolute bottom-2 right-2 w-3 h-3 border-b border-r" style={{ borderColor: 'rgba(212, 175, 55, 0.5)' }} />
                                
                                <div className="relative z-10 h-full flex flex-col items-center justify-between p-3 pt-5">
                                    {/* House number - Gold */}
                                    <span className="text-[10px] font-cinzel uppercase tracking-widest" style={{ color: '#D4AF37' }}>{t('houses.house')} {house.number}</span>
                                    
                                    <div className="flex flex-col items-center">
                                        {/* Symbol - Using elegant SVG planetary glyph */}
                                        <motion.div 
                                            className="mb-1 select-none" 
                                            animate={{ scale: [1, 1.05, 1] }} 
                                            transition={{ duration: 3, repeat: Infinity }}
                                        >
                                            <PlanetarySymbol planet={house.ruler} size={36} />
                                        </motion.div>
                                        {/* Chamber name - Gold gradient */}
                                        <span className="text-sm font-cinzel" style={{ background: 'linear-gradient(135deg, #F5D76E 0%, #D4AF37 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{house.name}</span>
                                    </div>
                                    
                                    {/* Planet indicators - Gold monochrome */}
                                    <div className="w-full space-y-1">
                                        {tenants && <div className="flex items-center justify-center gap-1"><span className="text-[7px]" style={{ color: 'rgba(212, 175, 55, 0.5)' }}>NAT</span><span className="text-[10px]" style={{ color: '#D4AF37' }}>{tenants}</span></div>}
                                        {visitors && <div className="flex items-center justify-center gap-1"><span className="text-[7px]" style={{ color: 'rgba(212, 175, 55, 0.7)' }}>{t('houses.now')}</span><span className="text-[10px]" style={{ color: '#F5D76E' }}>{visitors}</span></div>}
                                        {!tenants && !visitors && <p className="text-[8px] font-inter text-center leading-tight" style={{ color: 'rgba(212, 175, 55, 0.4)' }}>{house.theme}</p>}
                                    </div>
                                </div>
                                
                                {/* Bottom fade */}
                                <div className="absolute bottom-0 left-0 right-0 h-8 pointer-events-none" style={{ background: 'linear-gradient(to top, #050505 0%, transparent 100%)' }} />
                            </div>
                        </motion.div>
                    );
                })
                )}
                <div className="flex-shrink-0 w-4" />
            </div>
            
            <div className="flex justify-center mt-2">
                <motion.div className="flex items-center gap-1 text-[9px] font-inter text-primary/30" animate={{ x: [0, 5, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
                    <span>{t('houses.tap_for_insight')}</span><span>•</span><span>{t('houses.swipe')}</span><span>→</span>
                </motion.div>
            </div>
            
            {/* NASA JPL Attribution - 12 Houses only */}
            <div className="flex justify-center mt-3 px-4">
                <p className="text-[8px] font-inter text-center" style={{ color: 'rgba(212, 175, 55, 0.35)' }}>
                    {t('houses.nasa_attribution')}
                </p>
            </div>
            
            {/* Inline Modal */}
            {selectedHouse && (
                <HouseModal house={selectedHouse} insight={modalInsight} loading={modalLoading} isStreaming={isStreaming} onClose={closeModal} userName={userName} />
            )}
        </motion.div>
    );
}

function HouseModal({ house, insight, loading, isStreaming, onClose, userName }) {
    const { t, language } = useLanguage();
    const displayName = userName || "";
    const houseName = house.chamber || `${t('houses.house')} ${house.number}`;
    // Monochrome gold accent for all states
    const goldAccent = '#D4AF37';
    
    // Share functionality
    const shareRef = useRef(null);
    const [isSharing, setIsSharing] = useState(false);
    
    const handleShare = async () => {
        if (!shareRef.current || isSharing || !insight?.insight) return;
        
        setIsSharing(true);
        try {
            const canvas = await html2canvas(shareRef.current, {
                backgroundColor: "#0a0a0f",
                scale: 2,
                logging: false,
                useCORS: true,
            });
            
            const link = document.createElement("a");
            link.download = `house-${house.number}-reading-${Date.now()}.png`;
            link.href = canvas.toDataURL("image/png");
            link.click();
            
            if (navigator.vibrate) navigator.vibrate(100);
        } catch (err) {
            console.error("Share failed:", err);
        }
        setIsSharing(false);
    };
    
    // Translate condition label
    const getTranslatedCondition = (label) => {
        if (!label) return null;
        const translated = translateValue(label.text, CONDITION_TRANSLATIONS, language);
        return { ...label, text: translated };
    };
    
    // Translate zodiac sign
    const getTranslatedSign = (sign) => {
        return translateValue(sign, ZODIAC_TRANSLATIONS, language) || sign;
    };
    
    // Translate moon phase
    const getTranslatedMoonPhase = (phase) => {
        return translateValue(phase, MOON_PHASE_TRANSLATIONS, language) || phase;
    };
    
    // Translate planetary hour
    const getTranslatedPlanetaryHour = (planet) => {
        if (!planet) return "";
        // Extract planet name (e.g., "Saturn" from "Saturn Hour")
        const planetName = planet.replace(" Hour", "").replace(" hora", "");
        const translated = translateValue(planetName, PLANETARY_HOUR_TRANSLATIONS, language);
        const hourLabel = language === 'es' ? 'Hora' : 'Hour';
        return `${translated} ${hourLabel}`;
    };
    
    const translatedCondition = insight?.condition_label ? getTranslatedCondition(insight.condition_label) : null;
    const cuspLabel = language === 'es' ? 'Cúspide' : 'Cusp';
    
    return (
        <div className="fixed inset-0 z-50 flex flex-col">
            {/* Backdrop - clicking closes modal */}
            <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={onClose} />
            
            {/* Close button - Fixed at top for mobile accessibility */}
            <div className="relative z-10 flex justify-end p-4">
                <button 
                    onClick={onClose} 
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-black/60 border border-primary/30 hover:bg-black/80 transition-colors"
                    data-testid="house-modal-close-top"
                >
                    <span className="text-lg" style={{ color: '#D4AF37' }}>✕</span>
                </button>
            </div>
            
            {/* Scrollable content area */}
            <div className="relative z-10 flex-1 overflow-y-auto px-4 pb-8">
                <div className="max-w-md mx-auto">
                    {/* Obsidian & Gold Modal */}
                    <div className="rounded-2xl overflow-hidden" style={{ 
                        background: 'linear-gradient(180deg, #1a1a1a 0%, #050505 100%)', 
                        border: '1px solid rgba(212, 175, 55, 0.4)', 
                        boxShadow: '0 0 60px rgba(212, 175, 55, 0.1), inset 0 0 40px rgba(212, 175, 55, 0.02)' 
                    }} onClick={e => e.stopPropagation()} data-testid="house-detail-modal">
                        {/* Header */}
                        <div className="p-4 border-b" style={{ borderColor: 'rgba(212, 175, 55, 0.2)' }}>
                            <div className="flex items-center gap-3">
                                {/* House Number - Gold */}
                                <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ 
                                    background: 'rgba(212, 175, 55, 0.1)', 
                                    border: '2px solid rgba(212, 175, 55, 0.4)' 
                                }}>
                                    <span className="font-cinzel text-xl font-bold" style={{ color: goldAccent }}>{house.number}</span>
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-cinzel text-lg" style={{ color: goldAccent }}>{houseName}</h3>
                                    <p className="text-xs" style={{ color: 'rgba(212, 175, 55, 0.5)' }}>{insight ? insight.house_full_name : house.theme}</p>
                                </div>
                            </div>
                            {/* Status badges - Gold monochrome */}
                            {insight && translatedCondition && (
                                <div className="mt-3 flex items-center gap-2 flex-wrap">
                                    <span className="text-[10px] font-inter uppercase tracking-[0.15em] px-2 py-1 rounded" style={{ 
                                        background: 'rgba(212, 175, 55, 0.1)', 
                                        color: 'rgba(212, 175, 55, 0.7)', 
                                        border: '1px solid rgba(212, 175, 55, 0.2)' 
                                    }}>{getTranslatedSign(insight.sign_on_cusp)} {cuspLabel}</span>
                                    <span className="text-[10px] font-inter uppercase tracking-[0.15em] px-2 py-1 rounded flex items-center gap-1" style={{ 
                                        background: 'rgba(212, 175, 55, 0.15)', 
                                        color: '#F5D76E', 
                                        border: '1px solid rgba(212, 175, 55, 0.3)' 
                                    }}>
                                        <span>{translatedCondition.emoji}</span>{translatedCondition.text}
                                    </span>
                                </div>
                            )}
                        </div>
                    
                        <div className="p-5">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-8">
                                <div className="w-10 h-10 rounded-full border-2 animate-spin" style={{ borderColor: 'rgba(212, 175, 55, 0.3)', borderTopColor: '#D4AF37' }} />
                                <p className="mt-4 text-sm italic" style={{ color: 'rgba(212, 175, 55, 0.5)' }}>{language === 'es' ? `El Oráculo está entrando a la Casa ${house.number}...` : `The Oracle is entering House ${house.number}...`}</p>
                            </div>
                        ) : insight ? (
                            <div>
                                {/* Shareable Content Area - Captured by html2canvas */}
                                <div ref={shareRef} className="rounded-lg p-4" style={{ background: 'linear-gradient(180deg, #0a0a0f 0%, #050505 100%)' }}>
                                    {/* Branding Header for Share */}
                                    <div className="text-center mb-4 pb-3 border-b" style={{ borderColor: 'rgba(212, 175, 55, 0.2)' }}>
                                        <span className="text-[10px] font-inter uppercase tracking-[0.3em]" style={{ color: 'rgba(212, 175, 55, 0.4)' }}>Astral Belonging</span>
                                        <div className="flex items-center justify-center gap-2 mt-2">
                                            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ 
                                                background: 'rgba(212, 175, 55, 0.1)', 
                                                border: '1px solid rgba(212, 175, 55, 0.3)' 
                                            }}>
                                                <span className="font-cinzel text-lg" style={{ color: goldAccent }}>{house.number}</span>
                                            </div>
                                            <div className="text-left">
                                                <p className="font-cinzel text-sm" style={{ color: goldAccent }}>{houseName}</p>
                                                <p className="text-[9px]" style={{ color: 'rgba(212, 175, 55, 0.5)' }}>{insight.house_full_name}</p>
                                            </div>
                                        </div>
                                        {/* Status Badge */}
                                        {translatedCondition && (
                                            <div className="mt-2">
                                                <span className="text-[9px] font-inter uppercase tracking-[0.1em] px-2 py-0.5 rounded" style={{ 
                                                    background: 'rgba(212, 175, 55, 0.1)', 
                                                    color: '#F5D76E', 
                                                    border: '1px solid rgba(212, 175, 55, 0.2)' 
                                                }}>
                                                    {translatedCondition.emoji} {translatedCondition.text} • {getTranslatedSign(insight.sign_on_cusp)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Planet Grids - Gold monochrome */}
                                    <div className="grid grid-cols-2 gap-3 mb-4">
                                        <div className="p-3 rounded-lg" style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(212, 175, 55, 0.1)' }}>
                                            <p className="text-[9px] font-inter uppercase tracking-wider mb-2" style={{ color: 'rgba(212, 175, 55, 0.5)' }}>{t('houses.permanent_residents')}</p>
                                            <PlanetTags list={insight.tenants} style="tenant" accent={goldAccent} language={language} />
                                        </div>
                                        <div className="p-3 rounded-lg" style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(212, 175, 55, 0.1)' }}>
                                            <p className="text-[9px] font-inter uppercase tracking-wider mb-2" style={{ color: 'rgba(212, 175, 55, 0.5)' }}>{t('houses.current_visitors')}</p>
                                            <PlanetTags list={insight.visitors} style="visitor" accent={goldAccent} language={language} />
                                        </div>
                                    </div>
                                    
                                    {/* Landlord Info - Gold */}
                                    {insight.condition === "EMPTY" && insight.ruler && (
                                        <div className="p-3 rounded-lg mb-4" style={{ background: 'rgba(212, 175, 55, 0.05)', border: '1px solid rgba(212, 175, 55, 0.15)' }}>
                                            <p className="text-[9px] font-inter uppercase tracking-wider mb-1" style={{ color: 'rgba(212, 175, 55, 0.5)' }}>♔ {language === 'es' ? 'Regente' : 'Landlord'}</p>
                                            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
                                                <span style={{ color: '#D4AF37' }}>{insight.ruler.planet_symbol} {insight.ruler.planet}</span>
                                                <span style={{ color: 'rgba(255,255,255,0.5)' }}> {language === 'es' ? 'rige' : 'rules'} {getTranslatedSign(insight.ruler.sign)}</span>
                                                {insight.ruler.located_in_house && <span style={{ color: 'rgba(255,255,255,0.5)' }}> • {language === 'es' ? 'desde Casa' : 'from House'} {insight.ruler.located_in_house}</span>}
                                            </p>
                                        </div>
                                    )}
                                    
                                    {/* Chamber Vision - Main content */}
                                    <div className="p-4 rounded-lg" style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(212, 175, 55, 0.15)' }}>
                                        <p className="text-[10px] font-inter uppercase tracking-wider mb-2" style={{ color: 'rgba(212, 175, 55, 0.4)' }}>
                                            {t('houses.chamber_vision_for')} {displayName && <span style={{ color: 'rgba(212, 175, 55, 0.6)' }}>{displayName}</span>}
                                        </p>
                                        {insight.insight ? (
                                            <p className="text-sm font-inter leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)' }}>
                                                {parseMarkdown(insight.insight)}
                                                {isStreaming && <span className="inline-block w-2 h-4 ml-1 animate-pulse" style={{ background: 'rgba(212, 175, 55, 0.6)' }} />}
                                            </p>
                                        ) : (
                                            <div className="flex items-center gap-2 py-2">
                                                <div className="flex items-center gap-1">
                                                    <span className="w-2 h-2 rounded-full animate-bounce" style={{ background: '#D4AF37', animationDelay: '0ms' }} />
                                                    <span className="w-2 h-2 rounded-full animate-bounce" style={{ background: '#D4AF37', animationDelay: '150ms' }} />
                                                    <span className="w-2 h-2 rounded-full animate-bounce" style={{ background: '#D4AF37', animationDelay: '300ms' }} />
                                                </div>
                                                <span className="text-xs italic" style={{ color: 'rgba(212, 175, 55, 0.4)' }}>{language === 'es' ? 'El Oráculo está revelando tu verdad...' : 'The Oracle is revealing your truth...'}</span>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Cosmic Context - Gold tags */}
                                    {insight.cosmic_context && (
                                        <div className="mt-4 flex flex-wrap gap-2 justify-center">
                                            {insight.cosmic_context.moon_sign && <span className="px-2 py-1 rounded text-[9px]" style={{ background: 'rgba(212, 175, 55, 0.1)', color: 'rgba(212, 175, 55, 0.6)', border: '1px solid rgba(212, 175, 55, 0.2)' }}>☽ {getTranslatedSign(insight.cosmic_context.moon_sign)}</span>}
                                            {insight.cosmic_context.moon_phase && <span className="px-2 py-1 rounded text-[9px]" style={{ background: 'rgba(212, 175, 55, 0.1)', color: 'rgba(212, 175, 55, 0.6)', border: '1px solid rgba(212, 175, 55, 0.2)' }}>{getTranslatedMoonPhase(insight.cosmic_context.moon_phase)}</span>}
                                            {insight.cosmic_context.planetary_hour && <span className="px-2 py-1 rounded text-[9px]" style={{ background: 'rgba(212, 175, 55, 0.1)', color: 'rgba(212, 175, 55, 0.6)', border: '1px solid rgba(212, 175, 55, 0.2)' }}>{getTranslatedPlanetaryHour(insight.cosmic_context.planetary_hour)}</span>}
                                        </div>
                                    )}
                                    
                                    {/* Footer Branding */}
                                    <div className="text-center mt-4 pt-3 border-t" style={{ borderColor: 'rgba(212, 175, 55, 0.15)' }}>
                                        <span className="text-[8px] font-inter" style={{ color: 'rgba(212, 175, 55, 0.3)' }}>✧ {t('houses.stars_have_spoken')} ✧</span>
                                    </div>
                                </div>
                                
                                {/* Share Button (not captured in screenshot) */}
                                {insight.insight && !isStreaming && (
                                    <div className="flex justify-center mt-4">
                                        <motion.button 
                                            onClick={handleShare} 
                                            disabled={isSharing}
                                            className="px-4 py-2 rounded-lg text-sm font-inter flex items-center gap-2 disabled:opacity-50"
                                            style={{ 
                                                background: 'rgba(212, 175, 55, 0.1)', 
                                                color: 'rgba(212, 175, 55, 0.8)', 
                                                border: '1px solid rgba(212, 175, 55, 0.3)' 
                                            }}
                                            whileHover={{ scale: 1.02, background: 'rgba(212, 175, 55, 0.15)' }}
                                            whileTap={{ scale: 0.98 }}
                                            data-testid="house-share-btn"
                                        >
                                            {isSharing ? (
                                                <span className="animate-spin">◌</span>
                                            ) : (
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                                </svg>
                                            )}
                                            {t('houses.share_reading')}
                                        </motion.button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <span className="text-3xl" style={{ color: '#D4AF37' }}>✧</span>
                                <p className="mt-3 text-sm" style={{ color: 'rgba(212, 175, 55, 0.5)' }}>{language === 'es' ? 'No se pudo cargar la visión' : 'Could not load insight'}</p>
                            </div>
                        )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function PlanetTags({ list, style, accent, language }) {
    const noneLabel = language === 'es' ? 'Ninguno' : 'None';
    if (!list || list.length === 0) return <p className="text-xs text-foreground/30 italic">{noneLabel}</p>;
    
    // Translate planet name
    const getTranslatedPlanet = (planet) => {
        return translateValue(planet, PLANETARY_HOUR_TRANSLATIONS, language) || planet;
    };
    
    return (
        <div className="flex flex-wrap gap-1">
            {list.map((p, i) => (
                <span key={i} className={style === "tenant" ? "text-sm px-2 py-0.5 rounded bg-primary/10 text-primary/80 border border-primary/20" : "text-sm px-2 py-0.5 rounded"} style={style === "visitor" ? { background: `${accent}15`, color: accent, border: `1px solid ${accent}30` } : undefined}>
                    {p.symbol} {getTranslatedPlanet(p.planet)}
                </span>
            ))}
        </div>
    );
}

export default TwelveHousesCarousel;
